import { formatDate } from '@/lib/dateFormat';
import type { OnsiteLiveUser } from '@/types/oscar';

/**
 * The line at the top of a person's OnsiteLive account: what "Active" means for
 * them right now (Marty, 28/10/2026 - a starter's subscription groups were listed
 * and the screen said she was active, so it read as "reports are going out").
 *
 * Who gets subscription reports was checked on SQ04 the same day: the
 * Onsitelive.dbo.vwSubscriptions* views email members of the subscription's group
 * whose OnsiteLive account is ACTIVE. So an inactive login gets nothing, and an
 * active one gets reports from the next run.
 */
export interface AccountStatus {
  tone: 'info' | 'warn' | 'muted';
  text: string;
}

/** A person-sync account this recent, with no start date, may be a starter switched on too early. */
const NEW_ACCOUNT_DAYS = 30;

const day = (value: string) => value.slice(0, 10);

/** Today's date as yyyy-mm-dd, local time (the page is used in Australia, like the sync's Sydney date). */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function accountStatus(user: OnsiteLiveUser, today: string = localToday()): AccountStatus | null {
  if (!user.Active) {
    if (user.WaitingForStartDate) {
      const on = formatDate(day(user.WaitingForStartDate));
      return {
        tone: 'info',
        text: `Starts ${on}. This OnsiteLive login is switched off until then, so they can't sign in and get no subscription reports. `
          + `It is switched on automatically on ${on}; the department's subscription groups are added and reports start then.`,
      };
    }
    return { tone: 'muted', text: 'Inactive: this login cannot sign in to OnsiteLive, OnBudget or OnFlow, and gets no subscription reports.' };
  }

  if (user.PersonId == null) return null;

  if (user.PersonStartDate && day(user.PersonStartDate) > today) {
    return {
      tone: 'warn',
      text: `Starts ${formatDate(day(user.PersonStartDate))}, but this OnsiteLive login is already active: they can sign in and `
        + 'subscription reports go to them now. Untick Active below if they shouldn\'t have access yet - it then has to be '
        + 'switched back on by hand when they start.',
    };
  }

  if (!user.PersonStartDate && user.CreatedBy.endsWith(' (person sync)')) {
    const ageDays = (Date.parse(`${today}T00:00:00`) - Date.parse(`${day(user.CreatedAt)}T00:00:00`)) / 86_400_000;
    if (ageDays <= NEW_ACCOUNT_DAYS) {
      return {
        tone: 'warn',
        text: 'No start date in Oscar, so this new login was switched on straight away: they can sign in and subscription '
          + 'reports go to them now. If they haven\'t started yet, untick Active below - it then has to be switched back on '
          + 'by hand when they start. Enter the start date on the Profile before the login is made, and Oscar waits for it.',
      };
    }
  }

  return null;
}
